import React, { useCallback } from 'react';
import useOnReveal from '@hooks/use-on-reveal';
import useRestoreHpScrollPosition from '@hooks/cms/use-restore-hp-scroll-position';
import { CAMPAIGN_SWIPER_ID } from '@components/organisms/campaign/campaign-swiper';
import { CampaignBlock as ICampaignBlock, CategoryContainer } from '@interfaces/models/campaign';
import CampaignImg from '@components/molecules/campaign-block/components/campaign-img';
import CampaignCTA from '@components/molecules/campaign-block/components/campaign-cta';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { getCatalogDatalayerEntrypoint } from '@helpers/utils/catalog/catalog-tracking-utils';
import styles from './campaign-block.module.scss';

interface CampaignBlockProps extends ICampaignBlock {
  posX?: string;
  sectionIdx?: string;
  categoryContainer?: CategoryContainer;
}

const CampaignBlock: React.FC<CampaignBlockProps> = ({
  url,
  image,
  target,
  displayTitle: title,
  subtitle: subTitle,
  title: label,
  contentTypeUid: contentTypeUID,
  posX,
  sectionIdx,
  uid: campaignBlockId,
  category,
  ctaText,
  ctaPosition,
  ctaTextColor,
  ctaBackgroundColor: ctaBGColor,
  isHeroVersion,
  isSingle,
  categoryContainer,
}): React.JSX.Element => {
  const { sendAnalyticEvent, updateDataLayer } = useAnalyticEvents('cms_component');
  const { setHpScrollPosition } = useRestoreHpScrollPosition();
  const campaignBlockTrackingLabel = `${posX}_${sectionIdx ?? ''}`;
  const { containerRef } = useOnReveal(
    useCallback(() => {
      sendAnalyticEvent('cms_content_view', {
        category: 'cms_content_view',
        action: contentTypeUID,
        label: campaignBlockTrackingLabel,
        property: `${campaignBlockId}_${label}`,
      });
    }, [contentTypeUID, category?.category, campaignBlockId, label, posX, campaignBlockTrackingLabel, sectionIdx]),
    0.5,
    false,
  );

  const sendAnalyticEventWithObject = (eventDataObject) => {
    sendAnalyticEvent('cms_content_click', eventDataObject);
  };
  const trackCampaignBlockClick = (e) => {
    updateDataLayer(
      getCatalogDatalayerEntrypoint({
        source_category: 'cms_block',
        cms_block_id: campaignBlockId,
      }),
    );
    const eventData = {
      category: 'cms_content_click',
      action: contentTypeUID,
      label: campaignBlockTrackingLabel,
      property: `${campaignBlockId}_${label}`,
    };
    sendAnalyticEventWithObject(eventData);
    setHpScrollPosition(CAMPAIGN_SWIPER_ID);
    e.stopPropagation();
  };

  const onClick = (e, chip, chipXPos) => {
    const eventData = {
      category: 'cms_content_click',
      action: chip?.contentTypeUid,
      label: `${String(chipXPos + 1)}_${sectionIdx}`,
      property: `${chip?.uid}_${chip?.title}`,
    };
    sendAnalyticEventWithObject(eventData);
    e.stopPropagation();
    setHpScrollPosition(CAMPAIGN_SWIPER_ID);
  };

  const campaignCTAProps = {
    ctaPosition,
    ctaBGColor,
    ctaTextColor,
    title,
    subTitle,
    ctaText,
    isHeroVersion,
    isSingle,
    categoryContainer,
    sectionIdx,
  };

  return (
    <div
      ref={containerRef}
      className={styles.campaign__container}
    >
      <a
        className={styles.campaign__a}
        href={url}
        onClick={trackCampaignBlockClick}
        target={target || '_self'}
        itemProp="url"
      >
        <CampaignImg image={image} />
        <CampaignCTA
          onClick={onClick}
          {...campaignCTAProps}
        />
      </a>
    </div>
  );
};

export default CampaignBlock;
